import type { FetchPolicy } from 'apollo-client';

import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';

import UseTitleContentPropertiesForPublishedPageQuery, {
	type useTitleContentPropertiesForPublishedPageQuery$data,
	type useTitleContentPropertiesForPublishedPageQuery$variables,
} from './__generated__/useTitleContentPropertiesForPublishedPageQuery.graphql';
import { PreloadHomepageIdQuery } from './useTitleContentPropertiesForPublishedPage';
import type {
	useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery$data,
	useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery$variables,
} from './__generated__/useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery.graphql';

const preloadHomepageId = async (spaceKey: string) => {
	const res = await query<
		useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery$data,
		useTitleContentPropertiesForPublishedPagePreloadHomepageIdQuery$variables
	>({
		query: PreloadHomepageIdQuery,
		variables: {
			spaceKey,
		},
	});

	return res?.data?.space?.homepage?.id;
};

const preloadTitleContentProperty = (contentId: string, fetchPolicy: FetchPolicy = 'cache-first') =>
	query<
		useTitleContentPropertiesForPublishedPageQuery$data,
		useTitleContentPropertiesForPublishedPageQuery$variables
	>({
		query: UseTitleContentPropertiesForPublishedPageQuery,
		variables: {
			contentId,
			status: ['current', 'archived', 'draft'],
		},
		fetchPolicy,
	});

export const preloadPageTitleContentProperties = async ({
	contentId,
	contentIdPromise,
	spaceKey,
	fetchPolicy = 'cache-first',
}: {
	contentId: string;
	contentIdPromise?: Promise<string | undefined>;
	spaceKey: string;
	fetchPolicy?: FetchPolicy;
}) => {
	const promises: Promise<any>[] = [];

	if (!contentId) {
		const homepageIdPromise = contentIdPromise || preloadHomepageId(spaceKey);

		promises.push(
			homepageIdPromise.then((homepageId) => {
				return homepageId ? preloadTitleContentProperty(homepageId) : Promise.resolve({});
			}),
		);
	} else {
		promises.push(preloadTitleContentProperty(contentId, fetchPolicy));
	}

	return waitForAllReturnWhenError(promises);
};
